import {useState, useEffect} from "react";
import savedPromptsAPI from "../services/savedPromptsAPI";
import {ISavedPromptData} from "../types/savedPrompt";

export const useSavedPrompts = () => {
    const [isSavedPromptsOpen, setIsSavedPromptsOpen] = useState<boolean>(false);
    const [savedPrompts, setSavedPrompts] = useState<ISavedPromptData[]>([]);

    useEffect(() => {
        loadSavedPrompts();
    }, []);

    const loadSavedPrompts = async () => {
        try {
            const prompts = await savedPromptsAPI.getSavedPrompts();
            setSavedPrompts(prompts);
        } catch (error) {
            console.error("Failed to load saved prompts:", error);
        }
    };

    const handleSavePrompt = async (message: string) => {
        const isSaved = savedPrompts.some((prompt) => prompt.promptText === message);
        try {
            if (isSaved) {
                // Unsave the prompt
                const promptToDelete = savedPrompts.find(
                    (prompt) => prompt.promptText === message
                );
                if (promptToDelete?.id) {
                    await savedPromptsAPI.unsavePrompt(promptToDelete.id);
                }
                setSavedPrompts((prev) =>
                    prev.filter((prompt) => prompt.promptText !== message)
                );
            } else {
                // Save the prompt
                const savedPrompt = await savedPromptsAPI.savePrompt(message);
                setSavedPrompts((prev) => [...prev, savedPrompt]);
            }
        } catch (error) {
            console.error("Failed to save/unsave prompt:", error);
        }
    };

    const handleAddPrompt = async (text: string) => {
        try {
            const savedPrompt = await savedPromptsAPI.savePrompt(text);
            setSavedPrompts((prev) => [...prev, savedPrompt]);
        } catch (error) {
            console.error("Failed to add prompt:", error);
            throw error;
        }
    };

    const handleEditPrompt = async (promptId: number, newText: string) => {
        try {
            const oldPrompt = savedPrompts.find((prompt) => prompt.id === promptId);
            if (!oldPrompt) return;
            const updatedPrompt = await savedPromptsAPI.updatePrompt(
                promptId,
                newText
            );
            setSavedPrompts((prev) =>
                prev.map((prompt) =>
                    prompt.id === promptId ? updatedPrompt : prompt
                )
            );
        } catch (error) {
            console.error("Failed to edit prompt:", error);
            throw error;
        }
    };

    const handleDeletePrompt = async (promptId: number) => {
        try {
            const prompt = savedPrompts.find((prompt) => prompt.id === promptId);
            if (!prompt) return;
            await savedPromptsAPI.unsavePrompt(promptId);
            setSavedPrompts((prev) =>
                prev.filter((prompt) => prompt.id !== promptId)
            );
        } catch (error) {
            console.error("Failed to delete prompt:", error);
            throw error;
        }
    };

    const openSavedPrompts = () => {
        setIsSavedPromptsOpen(true);
    };

    const closeSavedPrompts = () => {
        setIsSavedPromptsOpen(false);
    };

    return {
        isSavedPromptsOpen,
        savedPrompts,
        openSavedPrompts,
        closeSavedPrompts,
        handleSavePrompt,
        handleAddPrompt,
        handleEditPrompt,
        handleDeletePrompt
    };
};
